'use client'

import { memo, useEffect, useRef, useState } from 'react'

import { stegaClean } from 'next-sanity'

import { Media } from '@/app/components/Media'
import { cn } from '@/app/utils/style'
import { SliderSection as SliderSectionProps } from '@/sanity.types'

// Memoize Media to prevent re-renders when only progress changes
const MemoizedMedia = memo(Media)

type SliderProps = {
  block: SliderSectionProps & { _key?: string }
  index: number
}

export default function SliderSection({ block }: SliderProps) {
  const slides = block.slides || []
  const autoplay = stegaClean(block.autoplay) ?? false
  const title = block.title
  const desktopLayout = stegaClean(block.desktopLayout) || 'horizontal'
  const verticalMargins = stegaClean(block.verticalMargins) || 'default'
  const isVertical = desktopLayout === 'vertical'
  const mobileFallback = block.mobileFallback

  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isManualMode, setIsManualMode] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Get current slide duration
  const currentDuration = slides[activeIndex]?.duration || 5000

  // Handle viewport intersection for autoplay
  useEffect(() => {
    if (!autoplay || !containerRef.current || isManualMode) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isPlaying) {
            setIsPlaying(true)
          }
        })
      },
      { threshold: 0.5 },
    )

    observer.observe(containerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [autoplay, isPlaying, isManualMode])

  // Single effect to handle autoplay slide advancement
  useEffect(() => {
    if (!isPlaying || isManualMode) return

    // Advance to next slide after duration
    const slideTimeout = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length)
    }, currentDuration)

    return () => {
      clearTimeout(slideTimeout)
    }
  }, [isPlaying, isManualMode, activeIndex, currentDuration, slides.length])

  // Handle manual slide change
  const handleSlideClick = (index: number) => {
    setActiveIndex(index)
    // Enter manual mode - disable autoplay permanently
    setIsManualMode(true)
    setIsPlaying(false)
  }

  if (!slides.length) return null

  return (
    <div
      ref={containerRef}
      className={cn(
        'content-container-dark bg-green-700',
        verticalMargins === 'tight' ? 'py-6xl lg:py-9xl lg:pt-6xl' : 'py-6xl lg:py-12xl',
      )}
    >
      <div
        className={cn(
          'mx-auto flex flex-col gap-3xl',
          isVertical ? 'lg:max-w-[1144px]' : 'max-w-[1144px]',
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-6xl',
            !isVertical && 'md:flex-col-reverse',
            isVertical && 'lg:flex-row lg:items-center lg:gap-6xl xl:gap-12xl',
          )}
        >
          {/* Media */}
          {slides.length > 0 && (
            <>
              {/* Mobile: Shows mobile fallback or first slide */}
              <div
                className={cn(
                  'w-full overflow-hidden rounded-md relative',
                  !mobileFallback && 'h-123.5',
                  isVertical ? 'lg:hidden max-w-[486px] mx-auto' : 'md:hidden',
                )}
              >
                {/* Render mobile fallback if available, otherwise first slide */}
                {(mobileFallback || slides[0]?.image) && (
                  <div
                    className={cn(
                      mobileFallback
                        ? 'relative [&_img]:object-contain [&_img]:w-full [&_img]:h-auto'
                        : 'absolute inset-0 *:h-full [&_img]:h-full [&_img]:object-cover [&_img]:object-center',
                    )}
                  >
                    <MemoizedMedia
                      image={mobileFallback || slides[0].image}
                      ignoreCrop
                      sizes="100vw"
                      coverMode={!mobileFallback}
                      applyLottieAspectRatio={true}
                    />
                  </div>
                )}
              </div>

              {/* Desktop: Conditional aspect ratio based on layout */}
              <div
                className={cn(
                  'hidden overflow-hidden rounded-md relative',
                  isVertical ? 'lg:block' : 'md:block',
                  isVertical
                    ? 'w-full max-w-[486px] lg:w-[324px] xl:w-[486px] mx-auto lg:mx-0 lg:flex-shrink-0 lg:aspect-486/567'
                    : 'w-full aspect-143/61',
                )}
              >
                {slides.map((slide, index) => {
                  const isActive = activeIndex === index

                  return (
                    <div
                      key={slide._key || index}
                      className={cn(
                        'absolute inset-0 transition-opacity duration-500 [&_img]:object-contain',
                        isActive ? 'opacity-100 z-10 visible' : 'opacity-0 z-0 invisible',
                      )}
                      style={{
                        contain: 'layout paint',
                        contentVisibility: isActive ? 'visible' : 'hidden',
                      }}
                    >
                      {slide.image && isActive && (
                        <MemoizedMedia
                          image={slide.image}
                          ignoreCrop
                          sizes={isVertical ? '(min-width: 1024px) 486px, 1144px' : '1144px'}
                          disableViewportControl={true}
                          shouldPlay={true}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {/* Tiles */}
          <div
            className={cn(
              'flex flex-col gap-3xl',
              !isVertical && 'md:flex-row md:flex-wrap',
              isVertical &&
                'w-full max-w-[486px] mx-auto lg:flex-1 lg:max-w-none lg:mx-0 lg:gap-md',
            )}
          >
            {title && (
              <p className="size-header-h4 text-center lg:text-left text-white-default mb-lg whitespace-pre-line md:basis-full md:order-[-1]">
                {title}
              </p>
            )}
            {slides.map((slide, index) => (
              <button
                type="button"
                key={slide._key || index}
                onClick={() => handleSlideClick(index)}
                className={cn(
                  'group relative text-left py-3xl px-4xl rounded-xs transition-colors overflow-hidden',
                  'bg-green-900 cursor-default pointer-events-none',
                  !isVertical &&
                    'md:flex-1 md:pointer-events-auto md:cursor-pointer md:bg-green-600',
                  isVertical && 'lg:pointer-events-auto lg:cursor-pointer lg:bg-green-600',
                  activeIndex === index && (isVertical ? 'lg:bg-green-900' : 'md:bg-green-900'),
                )}
              >
                {/* Animated progress border - hidden on mobile (respects layout breakpoint), hidden in manual mode */}
                {activeIndex === index && !isManualMode && isPlaying && (
                  <div
                    className={cn(
                      'absolute bottom-0 left-0 h-0.5 bg-neon-200',
                      isVertical ? 'hidden lg:block' : 'hidden md:block',
                    )}
                    style={{
                      width: '100%',
                      transformOrigin: 'left',
                      transform: 'scaleX(0)',
                      animation: `progressBar ${slide.duration || 5000}ms linear forwards`,
                    }}
                  />
                )}

                <div className="flex flex-col gap-md">
                  <p className="size-header-h5 text-white-default">{slide.heading}</p>
                  <p className="size-body-xs text-green-50">{slide.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
